import type {StateCreator, StoreApi} from "zustand/vanilla";
import {Access} from "../util/access/Access";
import {useAuthStore} from "../app-layout/authStore";
import {deleteElement, insertFirstElement, replaceElement} from "../util/ArrayUtil";
import {CrudRestResource, RestResource} from "./RestResource";
import Subscription from "../util/Subscription";
import {RemoteValue} from "../util/RemoteValue";
import {EntityType} from "./Model";
import {updatesConnection} from "./UpdatesConnection";
import Permission from "../util/access/Permission";
import {onRoleChange} from "../util/access/onRoleChange";

export interface EntityStore<Entities> {
    remoteValue: RemoteValue<Entities>;
    updateHandle: {entity: EntityType, officeId: number | null};
    refresh(): Promise<void>;
}

export class ResourceStoreBuilder<Entity, Entities, Store extends EntityStore<Entities>> {
    readonly setState: StoreApi<Store>['setState'];

    constructor(setState: StoreApi<Store>['setState']) {
        this.setState = setState;
    }

    rest(path: string) {
        return new RestResource<Entities>(path)
    }

    crud<Form>(path: string, idKey: keyof Entity) {
        return new CrudRestResource<Form, Entity, Entities>(path, idKey)
    }

    all(requiredPermissions: Permission, getAll: () => Promise<Entities>): RemoteValue<Entities> {
        return this.remoteValue(requiredPermissions, getAll);
    }

    remoteValue<Result>(requiredPermissions: Permission, fetch: () => Promise<Result>): RemoteValue<Result> {
        const remoteValue = new RemoteValue(fetch);
        if (this.allowEagerFetching(requiredPermissions)) {
            remoteValue.eagerLoad();
        }
        return remoteValue;
    }

    allowEagerFetching(requiredPermission: Permission): boolean {
        const access = new Access(useAuthStore.getState().user, null);
        return access.has(requiredPermission);
    }

    patch(patch: (prev: Entities) => Entities) {
        this.setState(store => ({
            remoteValue: store.remoteValue.patch(patch)
        }) as Partial<Store>);
    }
}

export class ArrayResourceStoreBuilder<Entity, Store extends EntityStore<Entity[]>> extends ResourceStoreBuilder<Entity, Entity[], Store> {
    createAndPatch<Form>(rest: CrudRestResource<Form, Entity, Entity[]>, subscription?: Subscription) {
        return async (form: Form) => {
            const newEntity = await rest.create(form);
            this.patch(prevEntities => insertFirstElement(prevEntities, newEntity));
            subscription?.notify();
            return newEntity;
        };
    }

    updateAndPatch<Form>(rest: CrudRestResource<Form, Entity, Entity[]>, subscription?: Subscription, entityCmp: (a: Entity, b: Entity) => boolean = (a, b) => a === b) {
        return async (entity: Entity, form: Form) => {
            const updatedEntity = await rest.update(entity, form);
            this.patch(prevEntities => replaceElement(prevEntities, (oldEntity) => entityCmp(oldEntity, entity), updatedEntity));
            subscription?.notify();
            return updatedEntity;
        }
    }

    deleteAndPatch<Form>(rest: CrudRestResource<Form, Entity, Entity[]>, subscription?: Subscription) {
        return async (entity: Entity) => {
            await rest.delete(entity);
            this.patch(prevEntities => deleteElement(prevEntities, entity));
            subscription?.notify();
        }
    }

    updateHandle(entity: EntityType): EntityStore<Entity[]>['updateHandle'] {
        return {entity, officeId: null};
    }
}

export function arrayResource<Entity, Store extends EntityStore<Entity[]>>(
    entityType: EntityType,
    build: (
        builder: ArrayResourceStoreBuilder<Entity, EntityStore<Entity[]>>,
        setState: StoreApi<Store>['setState'],
        getState: StoreApi<Store>['getState'],
    ) => Omit<Store, 'refresh' | 'updateHandle'>,
): StateCreator<Store> {
    return (set, get) => {
        const builder = new ArrayResourceStoreBuilder<Entity, Store>(set);

        return {
            refresh: refreshStoreFn(set, get),
            updateHandle: builder.updateHandle(entityType),
            ...build(builder, set, get),
        } as Store;
    }
}

export function refreshStoreFn<Store extends { remoteValue: RemoteValue<any> }>(
    set: StoreApi<Store>['setState'],
    get: StoreApi<Store>['getState'],
) {
    return async () => {
        const prevRemoteValue = get().remoteValue;
        const nextRemoteValue = prevRemoteValue.refresh();
        const patchedRemoteValue = await prevRemoteValue.patchRemote(nextRemoteValue);
        set({remoteValue: patchedRemoteValue} as Partial<Store>);
    }
}

export function startEntityStore(
    store: StoreApi<EntityStore<any[]>>,
    ...subscriptions: Subscription[]
) {
    const refresh = store.getState().refresh;
    onRoleChange(refresh);
    updatesConnection.addListener(update => {
        const updateHandle = store.getState().updateHandle;
        switch (update.type) {
            case 'update':
                if (update.entity === updateHandle.entity && update.officeId === updateHandle.officeId) {
                    refresh();
                }
                break;
            case 'reconnect':
                refresh();
                break;
        }
    });
    subscriptions.forEach(it => it.subscribe(refresh));
}
